import { useState, useEffect, useCallback } from "react";
import { Search, ChevronRight, Users, AlertTriangle, Baby } from "lucide-react";
import { Avatar, StatCard } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { ErrorAlert } from "../components/ErrorAlert";
import usersApi from "../api/users";

const RISK_META = {
  low: {
    label: "Low Risk",
    style: "bg-green-100 text-green-700 border-green-200",
    dot: "bg-green-500",
  },
  moderate: {
    label: "Moderate Risk",
    style: "bg-yellow-100 text-yellow-700 border-yellow-200",
    dot: "bg-yellow-500",
  },
  high: {
    label: "High Risk",
    style: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
  },
  unknown: {
    label: "Belum Dinilai",
    style: "bg-gray-100 text-gray-500 border-gray-200",
    dot: "bg-gray-400",
  },
};

function RiskBadge({ level }) {
  const meta = RISK_META[level] ?? RISK_META.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.style}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

export default function UserList({ onSelectUser }) {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await usersApi.getAll();
      setUsers(res.data?.data ?? []);
      setMeta(res.data?.meta ?? null);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Gagal memuat data pengguna",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search) ||
      u.doctor_name?.toLowerCase().includes(search.toLowerCase()),
  );

  const highRisk = users.filter((u) => u.risk_level === "high").length;
  const moderateRisk = users.filter((u) => u.risk_level === "moderate").length;

  if (loading)
    return (
      <div className="p-6">
        <PageLoader message="Memuat daftar pengguna..." />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={fetchUsers} />
      </div>
    );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="font-display font-800 text-2xl text-gray-900">
          Daftar Pengguna
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Total {meta?.total ?? users.length} ibu nifas terdaftar
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Total Pengguna"
          value={meta?.total ?? users.length}
          sub="ibu nifas terdaftar"
          icon={Users}
          color="bg-primary-50 text-primary-700"
        />
        <StatCard
          label="High Risk"
          value={highRisk}
          sub="perlu penanganan segera"
          icon={AlertTriangle}
          color="bg-red-50 text-red-600"
        />
        <StatCard
          label="Moderate Risk"
          value={moderateRisk}
          sub="perlu dipantau"
          icon={Baby}
          color="bg-yellow-50 text-yellow-600"
        />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cari nama, nomor HP, atau dokter..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">
                  Nama
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden md:table-cell">
                  Tgl. Melahirkan
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden md:table-cell">
                  Hari Nifas
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden lg:table-cell">
                  Dokter
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">
                  Risk Level
                </th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onSelectUser(user)}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar
                        initials={(user.name || "U").slice(0, 2).toUpperCase()}
                        size="sm"
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {user.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {user.age} tahun • {user.phone}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-sm text-gray-600">
                      {user.delivery_date
                        ? new Date(user.delivery_date).toLocaleDateString(
                            "id-ID",
                            { day: "numeric", month: "short", year: "numeric" },
                          )
                        : "—"}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700">
                      <Baby className="w-3.5 h-3.5" />
                      Hari ke-{user.nifas_day ?? "—"}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <p className="text-sm text-gray-600">
                      {user.doctor_name ?? "—"}
                    </p>
                    <p className="text-xs text-gray-400">
                      {user.hospital_name ?? ""}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <RiskBadge level={user.risk_level} />
                  </td>
                  <td className="px-5 py-4">
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-12 text-center text-sm text-gray-400"
                  >
                    {users.length === 0
                      ? "Belum ada pengguna terdaftar"
                      : "Tidak ada yang cocok dengan pencarian"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination info */}
        {meta && meta.last_page > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
            Halaman {meta.current_page} dari {meta.last_page} • Total{" "}
            {meta.total} pengguna
          </div>
        )}
      </div>
    </div>
  );
}
